import { AfterViewInit, ChangeDetectorRef, Component, ComponentRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { backdroopFadeIn, popupShootUp } from '../../../shared/animations/animations';
import { SopoAlertSettings } from '../../interfaces/alert';
import { BehaviorSubject, Subscription, combineLatest, distinctUntilChanged, filter, firstValueFrom } from 'rxjs';
import { ResizeService } from 'src/app/shared/services/resize/resize.service';
import { SplitScreenService } from 'src/app/game/splitScreen.service';

@Component({
    selector: 'app-sopo-alert',
    templateUrl: './sopo-alert.component.html',
    styleUrls: ['./sopo-alert.component.scss'],
    animations: [popupShootUp, backdroopFadeIn],
    host: {
        '[class]': 'backdropClass',
        '[style.zIndex]': 'zIndex',
    },
    standalone: false
})
export class SopoAlertComponent implements OnInit, AfterViewInit, OnDestroy {
    private _animationDone = new BehaviorSubject(false);
    private _alertClosed = new BehaviorSubject(false);
    private _destroy = new BehaviorSubject(false);
    private _fontSizeSubscription: Subscription;

    closing = false;

    dismissAfterTimeout: NodeJS.Timeout;
    outAnimation: boolean = false;

    style: {[key: string]: string} = {};

    backdropClass = "";

    zIndex = 1;

    constructor(
        @Inject('AlertSettings') public settings: SopoAlertSettings,
        private ref: ChangeDetectorRef,
        private resizeService: ResizeService,
        private splitScreenService: SplitScreenService,
    ) {
        this.createFontSizeSubscription();
    }

    ngOnInit() {
        if (this.settings.dismissAfter && this.settings.dismissAfter > 0) {
            this.dismissAfterTimeout = setTimeout(() => {
                this.dismiss();
            }, this.settings.dismissAfter);
        }
        if (this.settings.zIndex) this.zIndex = this.settings.zIndex;
    }
    
    ngAfterViewInit(): void { }

    getDestroyObservable() {
        return this._destroy.asObservable().pipe(
            filter(destroy => destroy),
            distinctUntilChanged(),
        )
    }

    private createFontSizeSubscription(): void {
        if (this.settings.isGameContainer) {
            const sopoPopupFontSize$ = this.resizeService.getSopoPopupFontSize();
            const gridView$ = this.splitScreenService.gridView$;
            const gamesListLength$ = this.splitScreenService.gameListLength$;
            this._fontSizeSubscription = combineLatest([gridView$, sopoPopupFontSize$, gamesListLength$]).subscribe(([gridView, fontSize, gamesListLength]) => {
                if (gridView && gamesListLength > 1 && gamesListLength <= 2) {
                    this.style.fontSize = `calc(${fontSize} * 0.8)`;
                } else if (gridView  && gamesListLength > 2) {
                    this.style.fontSize = `calc(${fontSize} * 0.6)`;
                } else {
                    this.style.fontSize = fontSize;
                }
                this.ref.markForCheck();
            });

        } else {
            this._fontSizeSubscription = this.resizeService.getSopoPopupFontSize().subscribe(fontSize => {
                this.style.fontSize = fontSize;
                this.ref.markForCheck();
            });
        }
    }

    backdropDismiss() {
        if (this.settings.backdropDismiss) this.dismiss();
    }

    animationDone(event) {
        if (event.triggerName === 'popupShootUp' && event.toState === 'void') {
            this._animationDone.next(true);
        }
    }

    outAnimationStarted(event: any) {
        if (event.triggerName === 'popupShootUp' && event.toState === 'void') {
            this.outAnimation = true;
            this.ref.detectChanges();
        }
    }

    getAlertClosed () {
        return this._alertClosed.asObservable().pipe(
            filter(value => value),
        );
    }

    dismissAndHandleAction(): void{
        this.dismiss();
        if (this.settings.buttonHandler) this.settings.buttonHandler();
    }

    async dismiss() {
        this.closing = true;
        this.ref.detectChanges();
        await firstValueFrom(this._animationDone.pipe(filter(value => value)));
        this._destroy.next(true);
    }
    
    ngOnDestroy(): void {
        if (this.dismissAfterTimeout) clearTimeout(this.dismissAfterTimeout);
        if (this._fontSizeSubscription) this._fontSizeSubscription.unsubscribe();
        this._alertClosed.next(true);
    }
}
